import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useStore, useNav } from '../store/StoreContext';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/useToast';
import { IconFileText, IconChevronRight, IconArrowRight, IconEdit } from '../components/icons';
import EstimateModal from '../components/modals/EstimateModal';
import EstimateExpandedDetails from '../components/forms/EstimateExpandedDetails';
import PhasePlanner from '../components/forms/PhasePlanner';
import PriorityPicker from '../components/ui/PriorityPicker';
import StatusDropdown from '../components/ui/StatusDropdown';
import AssigneePicker from '../components/ui/AssigneePicker';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { Button, StatusPill, listPanelCls, listRowCls, listRowSelectedCls, PageHeader, Toolbar, SearchInput, FilterChips, EmptyState, pageWrapCls } from '../components/ui';
import { useEstimateStatusChange, DateConfirmChips, seedDateChips } from '../hooks/useEstimateStatusChange';
import { useConfirm } from '../hooks/useConfirm';
import { getEstimateStatuses, DEFAULT_ESTIMATE_STATUSES } from '../lib/constants';
import db from '../lib/database';
import { formatPhone, phoneDigits } from '../lib/phone';
// Moves (2026-09-18): rows read the option label ("021-A" when the move has
// several options), the move's tentative marker on the date, and the
// Declined status (an option the client didn't pick — tucked like cancelled).
import { moveForEstimate, optionsForMove, optionLabel, fmtMoveDate, dateKeysOf, OPTION_TUCKED } from '../lib/moves';
import ClientPricingTab from '../components/forms/ClientPricingTab';
import { estimateTruckLabel } from '../lib/constants';

// ── Rows are MOVES (estimates-page.md pick 1A, 2026-09-19) ─────────────
// Every estimate is an option on a move; the page groups them by move_id
// and shows one row per move, led by its LEAD option (booked first, else
// the newest live one, else the newest). Options are lines inside the row.
const byNewest = (a, b) => (b.created_at || '').localeCompare(a.created_at || '');
const byLetter = (a, b) => (a.option_letter || '').localeCompare(b.option_letter || '') || (a.created_at || '').localeCompare(b.created_at || '');
export function groupEstimatesByMove(estimates, moves) {
    const map = new Map();
    for (const e of estimates) {
        const key = e.move_id || `legacy:${e.id}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(e);
    }
    return [...map.entries()].map(([key, opts]) => {
        const options = [...opts].sort(byLetter);
        const live = options.filter(o => !OPTION_TUCKED.has(o.status));
        const lead = options.find(o => o.status === 'booked') || [...live].sort(byNewest)[0] || [...options].sort(byNewest)[0];
        const move = options[0].move_id ? (moves || []).find(m => m.id === options[0].move_id) || null : null;
        return { key, move, options, live, tucked: options.filter(o => OPTION_TUCKED.has(o.status)), lead, newest: [...options].sort(byNewest)[0] };
    });
}
// "3 movers · 26' · 6–8 hrs" — what differs between options.
export function optionDiff(est) {
    const bits = [];
    const crew = Number(est.crew_size_needed) || 0;
    if (crew) bits.push(`${crew} mover${crew === 1 ? '' : 's'}`);
    const truck = estimateTruckLabel(est);
    if (truck) bits.push(truck);
    if (est.job_type === 'LONG_DISTANCE') { if (Number(est.bol_price) > 0) bits.push(`tariff $${Number(est.bol_price).toLocaleString()}`); }
    else {
        const lo = Number(est.min_hours) || 0, hi = Number(est.max_hours) || lo;
        if (hi) bits.push(lo && lo !== hi ? `${lo}–${hi} hrs` : `${hi} hrs`);
        if (Number(est.hourly_rate) > 0) bits.push(`$${Number(est.hourly_rate)}/hr`);
    }
    const packOn = est.packing_needed === 'yes' || est.packing_needed === 'partial' || est.packing_needed === 'full' || Number(est.packing_hours) > 0;
    if (packOn) bits.push('packing');
    const n = Array.isArray(est.planned_phases) ? est.planned_phases.length : 0;
    if (n > 1) bits.push(`${n} phases`);
    return bits.join(' · ');
}
export function optionPrice(est) {
    const lo = Number(est.total_min) || 0, hi = Number(est.total_max) || Number(est.total_estimate) || 0;
    if (!hi && !lo) return '';
    if (lo && hi && lo !== hi) return `$${lo.toLocaleString()} – $${hi.toLocaleString()}`;
    return `$${(hi || lo).toLocaleString()}`;
}

// ── Deposit transfer control (2026-06-09, Jaylen) ───────────────────────
// Lives next to the DEPOSIT PAID pill. Super-admin+ (transfer_deposits
// capability). Moves the paid deposit + signed-terms trail to another of
// the SAME client's estimates via db.transferDepositTrail — the answer to
// "destination changed after the deposit came in": build the new estimate
// normally, then transfer.
function DepositTransferButton({ est, allEstimates, onTransferred, showToast }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    // Portal + fixed positioning — an absolute dropdown inside the row
    // card paints BEHIND the following row cards (stacking-context clip,
    // same class of bug as the Modal/pac-container notes in CLAUDE.md).
    // Anchor rect is captured at open time; scroll/resize closes it.
    const [anchorRect, setAnchorRect] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const closeOnMove = () => setOpen(false);
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', closeOnMove, true);
        window.addEventListener('resize', closeOnMove);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', closeOnMove, true);
            window.removeEventListener('resize', closeOnMove);
        };
    }, [open]);

    const candidates = (allEstimates || [])
        // Declined options are tucked away like cancelled ones (2026-09-18).
        .filter(c => c.client_id === est.client_id && c.id !== est.id && c.status !== 'cancelled' && c.status !== 'declined' && !c.deposit_paid_at)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

    // In-app confirm (ConfirmDialog) instead of window.confirm — the
    // browser dialog looked off-brand and detached (Jaylen 2026-06-09).
    const [confirmTarget, setConfirmTarget] = useState(null);

    const doTransfer = async (target) => {
        setConfirmTarget(null);
        setBusy(true);
        try {
            await db.transferDepositTrail(est.id, target.id);
            showToast(`Deposit + terms moved to ${target.job_number}`);
            setOpen(false);
            if (onTransferred) await onTransferred();
        } catch (e) {
            showToast(`Transfer failed: ${e.message}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    const MENU_WIDTH = 288; // w-72

    return (
        <>
            <Button
                ref={btnRef}
                variant="secondary"
                size="sm"
                onClick={(e) => {
                    e.stopPropagation();
                    if (!open && btnRef.current) setAnchorRect(btnRef.current.getBoundingClientRect());
                    setOpen(o => !o);
                }}
                loading={busy}
                title="Transfer deposit + signed terms to another of this client's estimates"
                leftIcon={<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>}
            >
                Transfer
            </Button>
            {open && anchorRect && createPortal(
                <div
                    ref={menuRef}
                    className="fixed z-[200] bg-white border border-slate-200 rounded-lg shadow-xl w-72 py-1"
                    style={{
                        top: anchorRect.bottom + 4,
                        left: Math.max(8, Math.min(anchorRect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Transfer deposit + terms to…</div>
                    {candidates.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">
                            No other active estimates for this client — create the new estimate first, then transfer.
                        </div>
                    )}
                    {candidates.map(c => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => { setOpen(false); setConfirmTarget(c); }}
                            className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between gap-2"
                        >
                            <span className="text-xs font-bold text-slate-700">{c.job_number}</span>
                            <span className="text-[10px] text-slate-400">
                                {c.status}{c.move_date ? ` · ${c.move_date}` : ''}
                            </span>
                        </button>
                    ))}
                </div>,
                document.body,
            )}
            <ConfirmDialog
                open={!!confirmTarget}
                title={`Transfer ${est.job_number}'s deposit to ${confirmTarget?.job_number || '…'}?`}
                confirmLabel={`Transfer to ${confirmTarget?.job_number || '…'}`}
                onConfirm={() => doTransfer(confirmTarget)}
                onCancel={() => setConfirmTarget(null)}
            >
                The paid deposit{est.deposit_amount ? <span className="font-bold text-emerald-700"> (${Number(est.deposit_amount).toLocaleString()})</span> : ''} and signed terms move from{' '}
                <span className="font-bold text-slate-900">{est.job_number}</span> to{' '}
                <span className="font-bold text-emerald-700">{confirmTarget?.job_number}</span>.
                {' '}Both estimates keep a note of the transfer; nothing else on either estimate changes.
            </ConfirmDialog>
        </>
    );
}

// One slim line per option under a multi-option move row (1A). Live
// options first; declined ones fold under "Other quotes (n)".
function OptionLines({ group, statuses, canViewPricing, openOption }) {
    const [showTucked, setShowTucked] = useState(false);
    const live = group.options.filter(o => !OPTION_TUCKED.has(o.status));
    const tucked = group.options.filter(o => OPTION_TUCKED.has(o.status));
    const line = (o) => {
        const st = statuses.find(s => s.key === o.status) || { color: '#94a3b8', label: o.status };
        const lead = o.id === group.lead.id;
        return (
            <button key={o.id} type="button"
                onClick={(e) => { e.stopPropagation(); openOption(o); }}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full text-left flex items-center gap-2.5 py-1 text-[12px] hover:bg-slate-50 rounded-md px-1 -mx-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40">
                <span className={`font-mono text-[11px] font-bold w-6 ${lead ? 'text-slate-900' : 'text-slate-500'}`}>{o.option_letter || '?'}</span>
                <StatusPill color={st.color} label={st.label} />
                <span className={`min-w-0 truncate ${OPTION_TUCKED.has(o.status) ? 'text-slate-400' : 'text-slate-600'}`}>{optionDiff(o) || 'no pricing yet'}</span>
                {o.client_chosen_at && <span className="text-[9px] font-bold text-sky-700 whitespace-nowrap">CLIENT'S PICK</span>}
                <span className="ml-auto flex items-center gap-2 whitespace-nowrap">
                    {canViewPricing && <span className={`font-bold ${OPTION_TUCKED.has(o.status) ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{optionPrice(o)}</span>}
                    <span className="text-[11px] font-bold text-slate-400">Open ›</span>
                </span>
            </button>
        );
    };
    return (
        <div className="pl-6 mt-1 border-t border-dashed border-slate-200 pt-1.5" onClick={(e) => e.stopPropagation()}>
            {live.map(line)}
            {tucked.length > 0 && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setShowTucked(v => !v); }} className="text-[11px] font-bold text-slate-400 hover:text-slate-700 py-1">
                    Other quotes ({tucked.length}) {showTucked ? '▾' : '▸'}
                </button>
            )}
            {showTucked && tucked.map(line)}
        </div>
    );
}

function EstimatesPage() {
    const { can, user, displayName } = useAuth();
    const canEditClient = can('edit_clients');
    const canViewPricing = can('view_estimate_pricing');
    const canDelete = can('delete_records');
    const { estimates, clients, moves, crew, trucks, saveEstimate, removeEstimate, config, loadAll, confirmMoveDates } = useStore();
    const canTransferDeposits = can('transfer_deposits');
    const { openEstimateId, clearOpenEstimate, setCurrentPage } = useNav();
    // Pull estimate statuses from app config (falls back to DEFAULT_ESTIMATE_STATUSES).
    // This is the canonical source of truth — same pattern ClientsPage uses — so
    // filter pills, card header pills, and the in-row StatusPicker all stay in sync.
    // getEstimateStatuses merges defaults the saved Settings snapshot doesn't
    // know (declined, postponed) — see constants.js, 2026-09-21.
    const ESTIMATE_STATUSES = useMemo(() => getEstimateStatuses(config), [config]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [modal, setModal] = useState(null);
    // expandedId = a MOVE row key (group.key); activeOpt = the option tab
    // open inside it, per row (defaults to the lead option).
    const [expandedId, setExpandedId] = useState(null);
    const [activeOpt, setActiveOpt] = useState({});
    const [highlightId, setHighlightId] = useState(null);
    const pricingAutosaveRef = useRef(null);
    const { refreshMove } = useStore();
    // Pending status change → opens the ConfirmDialog before applying.
    // Shape: { est, newStatus, fromOpt, toOpt, clientName }
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    // Date chips on the quick-path Book confirm (2026-09-18): { load: true, … }
    const [bookChips, setBookChips] = useState({});
    const rowRefs = useRef({});
    const bookDateKeys = pendingStatusChange?.newStatus === 'booked' && pendingStatusChange.move ? dateKeysOf(pendingStatusChange.move) : [];
    const bookConfirmedKeys = bookDateKeys.filter(k => bookChips[k.key]).map(k => k.key);
    const byName = displayName || null;
    const { show: showToast } = useToast();
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();

    // When navigated in from another page (e.g. client file → "open estimate"),
    // auto-expand the target row, clear any active filter/search that would
    // hide it, scroll it into view, and flash a brief highlight ring.
    useEffect(() => {
        if (!openEstimateId) return;
        const target = estimates.find(e => e.id === openEstimateId);
        if (!target) {
            clearOpenEstimate();
            return;
        }
        setStatusFilter('all');
        setSearch('');
        const key = target.move_id || `legacy:${target.id}`;
        setExpandedId(key);
        setActiveOpt(a => ({ ...a, [key]: target.id }));
        setHighlightId(key);
        // Defer scroll to next frame so the row has been rendered
        requestAnimationFrame(() => {
            const node = rowRefs.current[key];
            if (node && typeof node.scrollIntoView === 'function') {
                node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
        const t = setTimeout(() => setHighlightId(null), 2200);
        clearOpenEstimate();
        return () => clearTimeout(t);
    }, [openEstimateId, estimates, clearOpenEstimate]);

    const groups = useMemo(() => groupEstimatesByMove(estimates, moves), [estimates, moves]);
    // Counts are per MOVE (lead option's status) — except Declined /
    // Cancelled, which count options in that state (a move is never
    // "declined"; one of its quotes is).
    const statusCounts = useMemo(() => {
        const counts = { all: groups.length };
        ESTIMATE_STATUSES.forEach(s => { counts[s.key] = 0; });
        groups.forEach(g => {
            counts[g.lead.status] = (counts[g.lead.status] || 0) + 1;
            g.options.forEach(o => { if (OPTION_TUCKED.has(o.status) && o !== g.lead) counts[o.status] = (counts[o.status] || 0) + 1; });
        });
        return counts;
    }, [groups, ESTIMATE_STATUSES]);
    // Header summary line (page-headers.md): live counts, not prose.
    const summary = useMemo(() => {
        const n = groups.length;
        const nOpts = estimates.length;
        const booked = statusCounts.booked || 0;
        const pending = statusCounts.pending_approval || 0;
        return `${n} move${n === 1 ? '' : 's'} · ${nOpts} quote${nOpts === 1 ? '' : 's'} · ${booked} booked · ${pending} pending approval`;
    }, [groups, estimates, statusCounts]);

    // Filtered ROWS: each row carries `shown` — the option the row leads
    // with under this filter (the matching declined one under Declined).
    const filtered = useMemo(() => {
        let list = groups.map(g => ({ ...g, shown: g.lead }));
        if (statusFilter !== 'all') {
            list = list.flatMap(g => {
                if (g.lead.status === statusFilter) return [g];
                if (OPTION_TUCKED.has(statusFilter)) { const o = g.options.find(x => x.status === statusFilter); return o ? [{ ...g, shown: o }] : []; }
                return [];
            });
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            // Phone queries match on digits only, so "(828) 844-4873" and
            // "8288444873" both hit. Gate on >=3 digits so short text
            // searches don't spuriously match every phone. (Same pattern as
            // ClientsPage / WalkthroughPortal — QA Module 2 BUG-2.)
            const qDigits = phoneDigits(search);
            list = list.filter(g => {
                const e = g.shown;
                const client = clients.find(c => c.id === e.client_id);
                const phoneHit = qDigits.length >= 3 && (
                    phoneDigits(client?.phone).includes(qDigits) ||
                    phoneDigits(client?.secondary_phone).includes(qDigits) ||
                    (Array.isArray(client?.phones) && client.phones.some(p => phoneDigits(p?.number).includes(qDigits)))
                );
                return (e.job_number || '').toLowerCase().includes(q)
                    || (client?.name || '').toLowerCase().includes(q)
                    || phoneHit
                    || (e.origin_address || '').toLowerCase().includes(q)
                    || (e.destination_address || '').toLowerCase().includes(q);
            });
        }
        // Newest move first (the old list was newest estimate first).
        return list.sort((a, b) => (b.newest.created_at || '').localeCompare(a.newest.created_at || ''));
    }, [groups, statusFilter, search, clients]);

    // + Alternate pricing on a row (2026-09-19): next letter on the move,
    // prefilled from the option the row is showing; opens its tab.
    const [addingOption, setAddingOption] = useState(null);
    const handleAlternatePricing = async (g, from) => {
        if (!g.move?.id) { showToast('This estimate predates moves — price it from the client file', 'error'); return; }
        setAddingOption(g.key);
        try {
            const src = from || g.lead;
            const seed = {};
            ['crew_size_needed', 'travel_crew_size', 'trucks_needed', 'extra_trucks', 'extra_truck_fee_waived', 'hourly_rate', 'estimated_hours', 'min_hours', 'max_hours', 'travel_fee', 'materials_fee', 'hold_fee', 'packing_needed', 'packing_crew_size', 'packing_rate', 'packing_hours', 'packing_min_hours', 'packing_max_hours', 'heavy_item_count', 'heavy_item_fee', 'removal_fee', 'bol_price', 'bol_reference', 'estimated_weight_lbs', 'total_estimate', 'total_min', 'total_max', 'internal_notes', 'is_draft']
                .forEach(k => { if (src[k] !== undefined) seed[k] = src[k]; });
            if (Array.isArray(src.planned_phases)) seed.planned_phases = src.planned_phases.map(p => ({ ...p, move_job_ids: [], move_job_id: null }));
            const created = await db.createOption(g.move.id, seed);
            await refreshMove(g.move.id);
            setExpandedId(g.key);
            setActiveOpt(a => ({ ...a, [g.key]: created.id }));
            showToast(`Option ${created.option_letter || ''} added — same dates and addresses; change what's different`);
        } catch (e) { showToast('Couldn\'t add an option: ' + e.message, 'error'); }
        finally { setAddingOption(null); }
    };

    const handleSave = async (data) => {
        try {
            await saveEstimate(data);
            showToast(data.id ? 'Estimate updated' : 'Estimate created');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
            throw e;
        }
    };

    // Destructive action — gated on the delete_records capability (default
    // super_admin + owner). Permission is gated twice: the button only
    // renders when canDelete is true (see render below), and this handler
    // re-checks before calling the store. In-app confirm (QA M2 NEW-9
    // native-dialog sweep) — deletion is not reversible from the UI.
    // `siblings` (2026-09-22): the move's OTHER options when deleting one
    // option of several — the copy talks about the option, the gentler path
    // is Declined (not Cancelled), and the survivors are re-lettered.
    const handleDeleteEstimate = async (est, { siblings = null } = {}) => {
        if (!canDelete) return;
        const client = clients.find(c => c.id === est.client_id);
        const label = `${est.job_number || '(no job #)'} — ${client?.name || 'Unknown'}`;
        const isOption = Array.isArray(siblings) && siblings.length > 0;
        const letter = est.option_letter || '?';
        const keepList = isOption ? siblings.map(o => o.option_letter || '?').sort().join(', ') : '';
        const sent = !!(est.written_estimate_token || est.terms_token || est.deposit_paid_at);
        const choice = await confirmDialog({
            title: isOption ? `Delete option ${letter} of ${est.job_number || 'this estimate'}?` : `Delete ${est.job_number || 'this estimate'} for ${client?.name || 'Unknown'}?`,
            body: isOption
                ? `Option ${letter} (${optionDiff(est) || 'no pricing yet'}${canViewPricing && optionPrice(est) ? ` · ${optionPrice(est)}` : ''}) is permanently removed. Option${siblings.length > 1 ? 's' : ''} ${keepList} stay${siblings.length > 1 ? '' : 's'} on ${client?.name || 'the client'}'s move${sent ? '' : ', re-lettered in order'}.${sent ? ` This option has a link or deposit on it — the client may have seen "Option ${letter}".` : ''} This can't be undone.`
                : `${label} and its pricing, phases and signed documents are permanently removed. ${client?.name || 'The client'}'s record and any jobs already on the dispatch board are kept. This can't be undone.`,
            confirmLabel: isOption ? `Delete option ${letter}` : `Delete ${est.job_number || 'estimate'}`,
            danger: true,
            // Gentler path (2B): a lost deal is usually a cancelled estimate,
            // not a deleted one — same status change the row picker makes.
            // For one option of several the gentle path is Declined (it
            // folds under "Other quotes" and stays on file).
            secondaryLabel: isOption
                ? (est.status !== 'declined' && est.status !== 'cancelled' ? 'Mark declined instead' : undefined)
                : (est.status !== 'cancelled' ? 'Mark cancelled instead' : undefined),
        });
        if (choice === 'secondary') {
            try {
                await handleSave({ ...est, status: isOption ? 'declined' : 'cancelled' });
                showToast(isOption ? 'Status → Declined' : 'Status → Cancelled');
            } catch (e) {
                showToast('Error: ' + (e.message || 'Save failed'), 'error');
            }
            return;
        }
        if (!choice) return;
        try {
            await removeEstimate(est.id);
            showToast(isOption ? `Option ${letter} deleted` : 'Estimate deleted');
            if (expandedId === (est.move_id || `legacy:${est.id}`)) setActiveOpt(a => { const n = { ...a }; delete n[est.move_id || `legacy:${est.id}`]; return n; });
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        }
    };

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Estimates"
                summary={summary}
                help={<>
                    <p>Every <span className="font-semibold">move</span> that's been priced lives here as one row. A move can carry more than one quote — <span className="font-semibold">Option A / Option B</span> — listed inside the row; <span className="font-semibold">Alternate pricing</span> adds another. New estimates start from a client's <span className="font-semibold">New Estimate</span> tab; everything after that happens here.</p>
                    <p><span className="font-semibold">The status ladder:</span> Building → Ready → Quoted → Pending Approval → Follow-Up → <span className="text-emerald-600 font-semibold">Booked</span> → Completed (or Cancelled). Marking one <span className="font-semibold">Booked</span> pushes its planned phases to the dispatch board; when the final phase finishes, the estimate completes itself.</p>
                    <p>Each row: the <span className="font-semibold">status pill</span> (click to change — risky moves get a confirm; a tentative date never blocks a booking, and the Book confirm inside the expanded row lets you tick which dates are confirmed with the client), the client's <span className="font-semibold">priority flag</span>, and the <span className="font-semibold">assignee</span> — who's covering this estimate. A <span className="font-semibold">~</span> before a date means it's still tentative; <span className="font-semibold">021-A / 021-B</span> are two prices for the same move, and the one the client didn't pick shows as Declined. Non-admins only see estimates assigned to them or that they created.</p>
                    <p>Click a row to expand it: the move's dates and route, a tab per option, that option's pricing (edits save as you go), the phase planner, crew notes, and the written-estimate link — one document per move that shows every option and lets the client pick.</p>
                </>}
            />

            {/* Search + status chips — one toolbar card (page-headers.md). */}
            <Toolbar>
                <SearchInput value={search} onChange={setSearch} placeholder="Search by job #, client, phone, or address..." />
                <FilterChips
                    ariaLabel="Filter by estimate status"
                    items={[
                        { key: 'all', label: 'All', count: statusCounts.all },
                        ...ESTIMATE_STATUSES.map(s => ({ key: s.key, label: s.label, count: statusCounts[s.key] || 0, color: s.color, bg: s.bg })),
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                />
            </Toolbar>

            {filtered.length === 0 ? (
                estimates.length === 0 ? (
                    <EmptyState
                        icon={<IconFileText size={28}/>}
                        title="No estimates yet"
                        hint="Open a client file and use the New Estimate tab to create the first one."
                        action={<Button size="sm" onClick={() => setCurrentPage('clients')}>Go to Clients</Button>}
                    />
                ) : (
                    <EmptyState
                        icon={<IconFileText size={28}/>}
                        title="No estimates match"
                        hint="Nothing matches this search and status filter. Clear them to see the full list."
                        action={<Button variant="tertiary" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear filters</Button>}
                    />
                )
            ) : (
                /* Ruled list — one panel, hairline rows (lists.md §2). */
                <div className={listPanelCls}>
                    {filtered.map(g => {
                        // The row reads from the option it's showing (the lead,
                        // or the matching declined one under that filter).
                        const est = g.shown;
                        const client = clients.find(c => c.id === est.client_id);
                        const rowMove = g.move || moveForEstimate(est, moves);
                        const dateTentative = !!rowMove?.load_tentative;
                        const moveDate = est.move_date ? fmtMoveDate(est.move_date, { tentative: dateTentative, year: true }) : null;
                        const shortNum = (est.job_number || '').replace(/^BBM-\d{4}-/, '') || '—';
                        const isExpanded = expandedId === g.key;
                        const isHighlighted = highlightId === g.key;
                        const phaseCount = Array.isArray(est.planned_phases) ? est.planned_phases.length : 0;
                        const multi = g.options.length > 1;
                        const active = g.options.find(o => o.id === activeOpt[g.key]) || est;
                        const chosen = g.options.find(o => o.client_chosen_at) || null;
                        return (
                            <div key={g.key}
                                ref={el => { rowRefs.current[g.key] = el; }}
                                data-testid="estimate-row"
                                data-estimate-id={est.id}
                                data-move-id={g.move?.id || ''}
                                data-job-number={est.job_number}
                                className={`${listRowCls} ${isExpanded ? listRowSelectedCls : ''} ${isHighlighted ? 'ring-2 ring-inset ring-red-400' : ''}`}>
                                <div className="cursor-pointer flex items-stretch min-h-[64px]" onClick={() => setExpandedId(isExpanded ? null : g.key)}>
                                    {/* Status block — the LEAD option's status; the same picker
                                        the Clients page uses. 110px so "BOOKED" / "CANCELLED" fit. */}
                                    <div className="flex-shrink-0 w-[110px] relative" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                                        <StatusDropdown
                                            value={est.status}
                                            statuses={ESTIMATE_STATUSES}
                                            size="block"
                                            blockLabel={multi ? `Status · ${est.option_letter || ''}` : 'Status'}
                                            blockRounding="rounded-none"
                                            chevronBelow
                                            onChange={(newStatus) => {
                                                if (newStatus === est.status) return;
                                                const fromOpt = ESTIMATE_STATUSES.find(s => s.key === est.status) || { label: est.status, color: '#94a3b8' };
                                                const toOpt = ESTIMATE_STATUSES.find(s => s.key === newStatus) || { label: newStatus, color: '#94a3b8' };
                                                // Rule 4 (2026-09-18): a tentative date never gates a
                                                // booking — the Book confirm carries the date chips.
                                                const move = newStatus === 'booked' ? rowMove : null;
                                                setPendingStatusChange({ est, newStatus, fromOpt, toOpt, clientName: client?.name || 'this client', move });
                                                setBookChips(seedDateChips(move));
                                            }}
                                        />
                                    </div>
                                    {client && (
                                        <div className="flex-shrink-0 w-[90px]" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                                            <PriorityPicker
                                                client={client}
                                                editable={canEditClient}
                                                variant="estimates-row"
                                                onChanged={() => showToast(`${client.name} priority updated`)}
                                            />
                                        </div>
                                    )}
                                    <div className="flex-shrink-0 w-[120px]" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                                        <AssigneePicker
                                            estimate={est}
                                            variant="estimates-row"
                                            onChanged={() => showToast('Assignee updated')}
                                        />
                                    </div>
                                    <div className="flex-1 p-4 flex flex-col gap-2 min-w-0 justify-center">
                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <IconChevronRight size={16}
                                                className={`text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}/>
                                            <span className="font-mono text-xs text-slate-400 font-bold whitespace-nowrap" title={est.job_number || ''}>{shortNum}</span>
                                            <span className="font-semibold text-slate-900 truncate">{client?.name || 'Unknown Client'}</span>
                                            {client?.phone && <span className="text-xs text-slate-400 hidden sm:inline">{formatPhone(client.phone)}</span>}
                                            {multi && (
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap" title={g.options.map(o => `${o.option_letter}: ${optionDiff(o)} · ${optionPrice(o)}`).join('\n')}>
                                                    {g.options.length} OPTIONS
                                                </span>
                                            )}
                                            {chosen && chosen.status !== 'booked' && (
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-50 text-sky-700 border border-sky-200 whitespace-nowrap" title={`The client picked option ${chosen.option_letter} on the written estimate ${new Date(chosen.client_chosen_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}>
                                                    CLIENT CHOSE {chosen.option_letter}
                                                </span>
                                            )}
                                            {!multi && phaseCount > 1 && (
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                                                    {phaseCount} PHASES
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            {(est.deposit_paid_at || est.deposit_token) && (
                                                <span
                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold border whitespace-nowrap ${
                                                        est.deposit_paid_at
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                                            : 'bg-amber-50 text-amber-700 border-amber-300'
                                                    }`}
                                                    title={est.deposit_paid_at
                                                        ? `Deposit paid ${new Date(est.deposit_paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                                        : 'Deposit link sent — not paid yet'}
                                                >
                                                    {est.deposit_paid_at ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    )}
                                                    {Number(est.deposit_amount) > 0 && (
                                                        <span className="text-[12px]">${Number(est.deposit_amount).toLocaleString()}</span>
                                                    )}
                                                    <span>{est.deposit_paid_at ? 'DEPOSIT PAID' : 'DEPOSIT SENT'}</span>
                                                </span>
                                            )}
                                            {!(est.deposit_paid_at || est.deposit_token) && Number(est.bol_deposit) > 0 && (() => {
                                                const collected = est.bol_deposit_status === 'collected';
                                                const color = collected ? '#059669' : '#d97706';
                                                const who = collected ? (est.bol_deposit_collected_by || 'COLLECTED') : 'REQUESTED';
                                                const when = collected && est.bol_deposit_collected_at
                                                    ? new Date(est.bol_deposit_collected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                    : null;
                                                return (
                                                    <span title={collected
                                                        ? `BOL deposit collected${est.bol_deposit_collected_by ? ` by ${est.bol_deposit_collected_by}` : ''}${est.bol_deposit_method ? ` (${est.bol_deposit_method})` : ''}${when ? ` on ${when}` : ''}`
                                                        : 'BOL deposit requested — not collected yet'}>
                                                        <StatusPill color={color} label={`Deposit $${Number(est.bol_deposit).toLocaleString()} · ${who}`} />
                                                    </span>
                                                );
                                            })()}
                                            {est.deposit_paid_at && canTransferDeposits && (
                                                <DepositTransferButton
                                                    est={est}
                                                    allEstimates={estimates}
                                                    onTransferred={loadAll}
                                                    showToast={showToast}
                                                />
                                            )}
                                            {canViewPricing && optionPrice(est) && <span className={`text-sm font-bold ${est.status === 'declined' ? 'text-slate-400 line-through' : 'text-green-600'}`}>{optionPrice(est)}</span>}
                                            {moveDate && (
                                                <span className={`text-xs ${dateTentative ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}
                                                    title={dateTentative ? 'Load date still tentative — confirm it on the move' : undefined}>
                                                    {moveDate}
                                                </span>
                                            )}
                                            {g.move && canEditClient && (
                                                <Button variant="secondary" size="sm" loading={addingOption === g.key}
                                                    onClick={(e) => { e.stopPropagation(); handleAlternatePricing(g, est); }}
                                                    title="Price this move another way — a new lettered option with the same dates and addresses">
                                                    Alternate pricing
                                                </Button>
                                            )}
                                            <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setModal(est); }}
                                                title="Open full estimate editor"
                                                data-testid="estimate-edit-btn"
                                                leftIcon={<IconEdit size={12} />}>
                                                Edit
                                            </Button>
                                            {canDelete && (
                                                <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteEstimate(est, multi ? { siblings: g.options.filter(o => o.id !== est.id) } : {}); }}
                                                    title={multi ? `Delete option ${est.option_letter || ''}` : 'Delete estimate'}>
                                                    Delete
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                        {(est.origin_address || est.destination_address) && (
                                            <div className="pl-6 flex items-center gap-2 text-xs text-slate-500">
                                                {est.origin_address && <span className="truncate max-w-[250px]">{est.origin_address}</span>}
                                                {est.origin_address && est.destination_address && <IconArrowRight size={12} className="flex-shrink-0 text-slate-300"/>}
                                                {est.destination_address && <span className="truncate max-w-[250px]">{est.destination_address}</span>}
                                            </div>
                                        )}
                                        {/* Option lines (1A): one per live option; declined ones fold. */}
                                        {multi && (
                                            <OptionLines
                                                group={g}
                                                statuses={ESTIMATE_STATUSES}
                                                canViewPricing={canViewPricing}
                                                openOption={(o) => { setExpandedId(g.key); setActiveOpt(a => ({ ...a, [g.key]: o.id })); }}
                                            />
                                        )}
                                    </div>{/* close flex-col main content */}
                                </div>{/* close cursor-pointer flex items-stretch */}
                                {isExpanded && (
                                    <div className="border-t border-slate-100">
                                        {/* 2A: the move card + summary (EstimateExpandedDetails)
                                            for the ACTIVE option, a tab per option above it, and
                                            the option's pricing editor (autosaving) below. */}
                                        {g.move && (
                                            <div className="px-4 pt-3 flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Pricing options">
                                                {g.options.map(o => {
                                                    const on = o.id === active.id;
                                                    const st = ESTIMATE_STATUSES.find(s => s.key === o.status) || { color: '#94a3b8', label: o.status };
                                                    return (
                                                        <button key={o.id} type="button" role="tab" aria-selected={on}
                                                            onClick={() => setActiveOpt(a => ({ ...a, [g.key]: o.id }))}
                                                            className={`h-8 px-3 rounded-full border text-[12px] font-bold inline-flex items-center gap-2 transition-colors ${on ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-500'} ${OPTION_TUCKED.has(o.status) && !on ? 'opacity-60' : ''}`}>
                                                            <span className="w-2 h-2 rounded-full" style={{ background: st.color }} aria-hidden="true" />
                                                            Option {o.option_letter || '?'}{canViewPricing && optionPrice(o) ? ` · ${optionPrice(o)}` : ''}
                                                        </button>
                                                    );
                                                })}
                                                {canEditClient && (
                                                    <Button size="sm" variant="secondary" loading={addingOption === g.key} onClick={() => handleAlternatePricing(g, active)}>+ Alternate pricing</Button>
                                                )}
                                                {/* Delete the ACTIVE option (Jaylen 2026-09-22: an option
                                                    B nobody meant to add had no way out — the row's
                                                    Delete only reaches the lead). Only when the move
                                                    keeps another option; the row's Delete handles the
                                                    last one. */}
                                                {canDelete && g.options.length > 1 && (
                                                    <Button size="sm" variant="danger" className="ml-auto"
                                                        onClick={() => handleDeleteEstimate(active, { siblings: g.options.filter(o => o.id !== active.id) })}>
                                                        Delete option {active.option_letter || ''}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        <EstimateExpandedDetails key={active.id} est={active} saveEstimate={handleSave} showToast={showToast} showAlternatePricing={!g.move} />
                                        {g.move && client && canViewPricing && (
                                            <div className="px-4 pb-2">
                                                <div className="rounded-xl border border-slate-200 bg-white p-4">
                                                    <ClientPricingTab
                                                        key={active.id}
                                                        client={client}
                                                        form={client}
                                                        set={() => {}}
                                                        config={config}
                                                        trucks={trucks}
                                                        readOnly={!canEditClient}
                                                        saveEstimate={saveEstimate}
                                                        estimates={estimates.filter(e => e.client_id === client.id)}
                                                        editEstimate={active}
                                                        autosaveRef={pricingAutosaveRef}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="px-4 pb-4">
                                            <PhasePlanner
                                                key={active.id}
                                                estimate={active}
                                                showToast={showToast}
                                                stripHidden={!!(g.move && client && canViewPricing)}
                                                onBeforePush={async () => { const a = pricingAutosaveRef.current; if (a?.pending?.()) return a.flush(); return true; }}
                                                onUpdated={async (updated) => {
                                                    try { await saveEstimate(updated); } catch (e) { /* already toasted */ }
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {modal && <EstimateModal estimate={modal} clients={clients} crew={crew} trucks={trucks} onClose={() => setModal(null)} onSave={async (data) => { await handleSave(data); setModal(null); }} onDelete={async (id) => { await removeEstimate(id); showToast('Estimate deleted'); setModal(null); }} />}
            {/* Estimate-status change confirmation — same styling as the
                priority change modal (ConfirmDialog). Fires whenever the
                row's StatusDropdown commits a new status; user has to
                confirm before the save lands. */}
            <ConfirmDialog
                open={!!pendingStatusChange}
                title={!pendingStatusChange ? 'Change status?'
                    : `Change ${pendingStatusChange.clientName}'s status to ${pendingStatusChange.toOpt.label}?`}
                confirmLabel={!pendingStatusChange ? 'Change status'
                    : bookDateKeys.length ? `Book — ${bookConfirmedKeys.length} confirmed`
                    : `Change to ${pendingStatusChange.toOpt.label}`}
                danger={pendingStatusChange?.newStatus === 'cancelled' || (pendingStatusChange?.newStatus === 'postponed' && pendingStatusChange?.fromOpt?.key === 'booked')}
                onConfirm={async () => {
                    if (!pendingStatusChange) return;
                    const { est, newStatus, toOpt, move } = pendingStatusChange;
                    const keys = bookConfirmedKeys;
                    setPendingStatusChange(null);
                    try {
                        // 2026-09-18: booking never touches the dates (rule 4);
                        // the ✓ chips are logged as confirmations first, then
                        // the booking lands. Nothing here blocks the booking.
                        if (newStatus === 'booked' && move && keys.length) await confirmMoveDates(move.id, keys, byName);
                        await handleSave({ ...est, status: newStatus });
                        showToast(`Status → ${toOpt.label}`);
                    } catch (e) {
                        showToast('Error: ' + (e.message || 'Save failed'), 'error');
                    }
                }}
                onCancel={() => setPendingStatusChange(null)}>
                {pendingStatusChange && (
                    <>
                        <span className="font-semibold text-slate-900">{pendingStatusChange.clientName}</span>'s estimate moves from{' '}
                        <span className="font-bold uppercase" style={{ color: pendingStatusChange.fromOpt.color }}>{pendingStatusChange.fromOpt.label}</span>{' '}
                        to{' '}
                        <span className="font-bold uppercase" style={{ color: pendingStatusChange.toOpt.color }}>{pendingStatusChange.toOpt.label}</span>.
                        {pendingStatusChange.newStatus === 'cancelled'
                            ? ' The estimate and its pricing are kept on file; any jobs already on the dispatch board are not removed.'
                            : pendingStatusChange.newStatus === 'booked'
                                ? ' Its planned phases are pushed to the dispatch board as unassigned jobs. Tap a date to mark it confirmed with the client or still tentative — either way the booking goes through.'
                                : pendingStatusChange.newStatus === 'postponed'
                                    ? (pendingStatusChange.fromOpt.key === 'booked'
                                        ? ' The job is on hold with no date: its legs come off the dispatch board and its dates are marked tentative. The quote, addresses and signed terms stay on file — set a date and mark it Booked again when they reschedule.'
                                        : ' The job is on hold with no date: its dates are marked tentative and it drops out of the capacity count. The quote stays on file — move it back to Quoted or Booked when they reschedule.')
                                    : ' The estimate itself is not changed; the audit log records the change.'}
                        {bookDateKeys.length > 0 && (
                            <div className="mt-3">
                                <DateConfirmChips dateKeys={bookDateKeys} state={bookChips} onChange={setBookChips} />
                            </div>
                        )}
                    </>
                )}
            </ConfirmDialog>
            {confirmElement}
        </div>
    );
}

export default EstimatesPage;
